import {
  createContext,
  FC,
  PropsWithChildren,
  useContext,
  useEffect,
  useState,
} from "react";
import { Canvg } from "canvg";

type ImageDataUrlKeys =
  | "vblLogo"
  | "block"
  | "close"
  | "radioButtonUnchecked"
  | "radioButtonChecked"
  | "thumbDown"
  | "thumbUp";

export interface ImageDataUrlValues {
  dataUrls: DataUrlsType;
}

type DataUrlsType = { [k in ImageDataUrlKeys]?: string };

const images: DataUrlsType = {
  vblLogo: "/VBL_Logo.png",
  block: "/block.svg",
  close: "/close.svg",
  radioButtonChecked: "/radio_button_checked.svg",
  radioButtonUnchecked: "/radio_button_unchecked.svg",
  thumbDown: "/thumb_down.svg",
  thumbUp: "/thumb_up.svg",
};

const ImageDataUrlContext = createContext({ dataUrls: {} });

export const useImageDataUrlContext = (): ImageDataUrlValues =>
  useContext(ImageDataUrlContext);

const transformSvgToDataUrl = async (svg: string): Promise<string> => {
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");

  if (context) {
    try {
      context.clearRect(0, 0, canvas.width, canvas.height);
      const c = await Canvg.from(context, svg);
      c.start();
      const dataUrl = canvas.toDataURL("image/png");
      c.stop();
      return dataUrl;
    } catch (e) {
      return "";
    }
  } else {
    return Promise.resolve(svg);
  }
};

const transformBlobToDataUrl = async (blob: Blob): Promise<string> =>
  new Promise((resolve) => {
    let reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.readAsDataURL(blob);
  });

const ImageDataUrlContextProvider: FC<PropsWithChildren<{}>> = (props) => {
  const [imageDataUrls, setImageDataUrls] = useState<
    ImageDataUrlValues["dataUrls"]
  >({});

  useEffect(() => {
    const fetchAndTransformImages = async (): Promise<{
      [k in string]: string;
    }> => {
      const fetchAndTransformImage = async (url: string): Promise<string> => {
        return fetch(url).then(async (r) => {
          if (url.endsWith(".svg")) {
            const svg = await r.text();
            return transformSvgToDataUrl(svg);
          } else {
            const blob = await r.blob();
            return transformBlobToDataUrl(blob);
          }
        });
      };

      return Promise.all(
        Object.entries(images).map(([key, url]) =>
          fetchAndTransformImage(url).then((dataUrl) => ({ [key]: dataUrl })),
        ),
      ).then((results) =>
        results.reduce((col, res) => ({ ...col, ...res }), {}),
      );
    };

    fetchAndTransformImages().then((images) => setImageDataUrls(images));
  }, []);

  return (
    <ImageDataUrlContext.Provider value={{ dataUrls: imageDataUrls }}>
      {props.children}
    </ImageDataUrlContext.Provider>
  );
};

export default ImageDataUrlContextProvider;
